import { useState } from 'react';
import { sampleReviews, StarRating, PlatformIcon, VerificationIcon, getFontFamily, normalizeReviewData } from './SharedComponents';
import { capitalizePlatformName } from '../../utils/platformNames';

// Helper function to check if a string is a valid image URL
const isValidImageUrl = (url) => {
    if (!url || typeof url !== 'string') return false;

    // Check if it's a data URI image
    if (url.startsWith('data:image/')) return true;

    // Check if it's a proper HTTP/HTTPS URL
    const isUrl = url.startsWith('http://') || url.startsWith('https://');
    if (!isUrl) return false;

    // Check for common image extensions
    const imageExtensions = /\.(jpg|jpeg|png|gif|bmp|webp|svg)(\?.*)?$/i;
    if (imageExtensions.test(url)) return true;

    // Check for known image hosting services (more comprehensive list)
    const imageHosts = new RegExp('(amazonaws\\.com|cloudfront\\.net|googleapis\\.com|googleusercontent\\.com|gravatar\\.com|unsplash\\.com|imgur\\.com|cloudinary\\.com|imagekit\\.io|uploadcare\\.com|cloudflare\\.com/[^/]*/image|facebook\\.com/[^/]*/picture|graph\\.facebook\\.com|fbcdn\\.net|twimg\\.com|linkedin\\.com/[^/]*/image|bing\\.com/[^/]*/image)', 'i');
    if (imageHosts.test(url)) return true;

    // For any other URL that might be an image, we'll try to load it
    // This is more permissive but safer for user-generated content
    return true;
};

// Helper function to generate initials from name
const generateInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(word => word.charAt(0)).join('').toUpperCase().slice(0, 2);
};

// List Preview
export const ListPreview = ({ reviews = [], previewMode = 'desktop', selectedPlatforms = [], widgetConfig = {} }) => {
    const [activeTab, setActiveTab] = useState('all');

    const displayReviews = (reviews.length > 0 && selectedPlatforms.length > 0) ? reviews : sampleReviews;

    // Create platform data from reviews with icon URLs
    const createPlatformData = () => {
        const platformStats = {};
        let totalRating = 0;
        let totalCount = 0;

        displayReviews.forEach(review => {
            const normalized = normalizeReviewData(review);
            const platformName = normalized.platform;
            const iconUrl = review.platform?.iconLink; // Get iconUrl from original review data

            if (!platformStats[platformName]) {
                platformStats[platformName] = { ratings: [], count: 0, iconUrl: iconUrl };
            }

            // If we don't have an iconUrl yet but this review has one, use it
            if (!platformStats[platformName].iconUrl && iconUrl) {
                platformStats[platformName].iconUrl = iconUrl;
            }

            platformStats[platformName].ratings.push(normalized.rating);
            platformStats[platformName].count++;
            totalRating += normalized.rating;
            totalCount++;
        });

        const platforms = [
            {
                id: 'all',
                name: 'All Reviews',
                rating: totalCount > 0 ? (totalRating / totalCount) : 0,
                count: totalCount,
                iconUrl: null // No specific icon for "All Reviews"
            }
        ];

        Object.entries(platformStats).forEach(([platform, stats]) => {
            const avgRating = stats.ratings.reduce((sum, rating) => sum + rating, 0) / stats.ratings.length;
            platforms.push({
                id: platform,
                name: platform.charAt(0).toUpperCase() + platform.slice(1),
                rating: avgRating,
                count: stats.count,
                iconUrl: stats.iconUrl // Include the iconUrl from review data
            });
        });

        return platforms;
    };

    const platformData = createPlatformData();

    // Filter reviews based on active tab
    const filteredReviews = activeTab === 'all'
        ? displayReviews
        : displayReviews.filter(review => {
            const normalized = normalizeReviewData(review);
            return normalized.platform.toLowerCase() === activeTab.toLowerCase();
        });

    // Generate container styles based on widgetConfig
    const getContainerStyles = () => {
        const backgroundConfig = widgetConfig.style?.background || {};
        const width = backgroundConfig.width || 800;
        const borderRadius = backgroundConfig.borderRadius ?? 16;
        const shadow = backgroundConfig.shadow ?? true;
        const colorScheme = widgetConfig.style?.colorScheme || 'light';

        return {
            width: `${width}px`,
            maxWidth: '100%',
            borderRadius: `${borderRadius}px`,
            boxShadow: shadow ? '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' : 'none',
            backgroundColor: backgroundConfig.color || (colorScheme === 'dark' ? '#1F2937' : '#FFFFFF'),
            fontFamily: getFontFamily(widgetConfig.style?.fontFamily || 'default')
        };
    };

    // Get style configuration values
    const styleConfig = widgetConfig?.style || {};
    const textSize = styleConfig?.review?.textSize || 16;
    const outlineColor = styleConfig?.review?.outlineColor || '#E5E5E5';
    const textColor = styleConfig?.review?.textColor || '#000000';
    const reviewerNameColor = styleConfig?.review?.reviewerNameColor || '#000000';
    const dateColor = styleConfig?.review?.dateColor || '#666666';

    // Widget configuration
    const widgetTitleConfig = widgetConfig?.widgetTitleConfig || { showTitle: true, title: 'Customer Reviews', caption: 'See what our customers are saying' };
    const headerConfig = widgetConfig?.headerConfig || { showHeader: true, elements: { rating: true, numberOfReviews: true, writeReviewButton: true, heading: true } };
    const tabsConfig = widgetConfig?.tabsConfig || { showTabs: true, elements: { icon: true, name: true, rating: true } };

    const overallRating = platformData.find(p => p.id === 'all');
    const colorScheme = widgetConfig.style?.colorScheme || 'light';

    return (
        <div
            className={`mx-auto ${colorScheme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}
            style={getContainerStyles()}
        >
            {/* Widget Title Section */}
            {widgetTitleConfig.showTitle && (
                <div className={`${previewMode === 'mobile' ? 'mb-4 p-4' : 'mb-6 p-6'}`}>
                    <h1
                        className={`${previewMode === 'mobile' ? 'mb-2' : 'mb-3'}`}
                        style={{
                            color: colorScheme === 'dark' ? '#FFFFFF' : '#000000',
                            fontSize: `${styleConfig?.widgetTitle?.titleSize || 24}px`,
                            fontWeight: styleConfig?.widgetTitle?.titleBold ? '700' : 'normal',
                            fontStyle: styleConfig?.widgetTitle?.titleItalic ? 'italic' : 'normal',
                            textAlign: styleConfig?.widgetTitle?.alignment || 'center'
                        }}
                    >
                        {widgetTitleConfig.title}
                    </h1>
                    {widgetTitleConfig.caption && (
                        <p
                            style={{
                                color: colorScheme === 'dark' ? '#D1D5DB' : '#666666',
                                fontSize: `${styleConfig?.widgetTitle?.captionSize || 15}px`,
                                fontWeight: styleConfig?.widgetTitle?.captionBold ? '700' : 'normal',
                                fontStyle: styleConfig?.widgetTitle?.captionItalic ? 'italic' : 'normal',
                                textAlign: styleConfig?.widgetTitle?.alignment || 'center'
                            }}
                        >
                            {widgetTitleConfig.caption}
                        </p>
                    )}
                </div>
            )}

            {/* Header Section */}
            {headerConfig.showHeader && (
                <div className={`text-center ${previewMode === 'mobile' ? 'mb-6 px-4' : 'mb-8 px-6'}`}>
                    {/* Platform Tabs */}
                    {tabsConfig.showTabs && (
                        <div className={`flex flex-wrap justify-center gap-2 ${previewMode === 'mobile' ? 'mb-4' : 'mb-6'}`}>
                            {platformData.slice(0, previewMode === 'mobile' ? 3 : platformData.length).map((platform) => (
                                <button
                                    key={platform.id}
                                    onClick={() => setActiveTab(platform.id)}
                                    className={`flex items-center space-x-2 rounded-md border transition-all duration-200 ${previewMode === 'mobile' ? 'px-3 py-2' : 'px-5 py-3'} ${activeTab === platform.id
                                        ? 'bg-gray-900 text-white border-gray-900'
                                        : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                                        }`}
                                >
                                    {platform.id !== 'all' && tabsConfig.elements.icon && (
                                        <PlatformIcon
                                            platform={platform.id}
                                            iconUrl={platform.iconUrl}
                                            className={previewMode === 'mobile' ? 'w-4 h-4' : 'w-5 h-5'}
                                        />
                                    )}
                                    {tabsConfig.elements.name && (
                                        <span className={`font-medium ${previewMode === 'mobile' ? 'text-sm' : ''}`}>{capitalizePlatformName(platform.name)}</span>
                                    )}
                                    {tabsConfig.elements.rating && (
                                        <span className={`font-bold ${previewMode === 'mobile' ? 'text-sm' : ''}`}>{platform.rating.toFixed(1)}</span>
                                    )}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Overall Rating Section */}
                    {(headerConfig.elements.rating || headerConfig.elements.numberOfReviews || headerConfig.elements.writeReviewButton) && (
                        <div className={`mx-auto bg-gray-50 rounded-lg ${previewMode === 'mobile' ? 'max-w-sm p-4 mb-6 space-y-3' : 'max-w-2xl p-6 mb-8 flex items-center justify-between'}`}>
                            {(headerConfig.elements.rating || headerConfig.elements.numberOfReviews) && (
                                <div className={`${previewMode === 'mobile' ? 'text-center' : 'flex items-center space-x-4'}`}>
                                    <div>
                                        <div className={previewMode === 'mobile' ? 'text-center' : 'text-left'}>
                                            {headerConfig.elements.heading && (
                                                <div className={`font-medium text-gray-600 mb-1 ${previewMode === 'mobile' ? 'text-xs' : 'text-sm'}`}>Overall Rating</div>
                                            )}
                                            <div className={`flex items-center ${previewMode === 'mobile' ? 'justify-center space-x-2' : 'space-x-3'}`}>
                                                {headerConfig.elements.rating && (
                                                    <>
                                                        <span className={`font-bold text-gray-900 ${previewMode === 'mobile' ? 'text-2xl' : 'text-3xl'}`}>{overallRating.rating.toFixed(1)}</span>
                                                        <div className="flex space-x-1">
                                                            {[1, 2, 3, 4, 5].map((star) => (
                                                                <svg
                                                                    key={star}
                                                                    className={`${previewMode === 'mobile' ? 'w-4 h-4' : 'w-6 h-6'} fill-current`}
                                                                    style={{
                                                                        color: star <= overallRating.rating
                                                                            ? (widgetConfig.style?.rating?.starsColor || '#F1C40F')
                                                                            : '#d1d5db'
                                                                    }}
                                                                    viewBox="0 0 24 24"
                                                                >
                                                                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                                                </svg>
                                                            ))}
                                                        </div>
                                                    </>
                                                )}
                                                {headerConfig.elements.numberOfReviews && (
                                                    <span className={`text-gray-500 ${previewMode === 'mobile' ? 'text-sm' : ''}`}>({overallRating.count.toLocaleString()})</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {headerConfig.elements.writeReviewButton && (
                                <button
                                    className={`text-white font-medium rounded-lg transition-colors duration-200 ${previewMode === 'mobile' ? 'px-4 py-2 text-sm w-full' : 'px-6 py-2'}`}
                                    style={{ backgroundColor: styleConfig?.accentColor || '#4A90E2' }}
                                >
                                    Write a review
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* List Layout */}
            <div className={`${previewMode === 'mobile' ? 'p-4' : 'px-6 pb-6'}`}>
                <div className={`space-y-4 overflow-y-auto ${previewMode === 'mobile' ? 'max-h-80' : 'max-h-96'}`}>
                    {filteredReviews.slice(0, 8).map((review) => {
                        const normalizedReview = normalizeReviewData(review);
                        return (
                            <div key={normalizedReview.id} className="border-b pb-3 last:border-b-0" style={{ borderColor: outlineColor }}>
                                <div className="flex items-start space-x-3">
                                    <div className="w-8 h-8 rounded-full flex-shrink-0 relative">
                                        {isValidImageUrl(normalizedReview.avatar) ? (
                                            <>
                                                <img
                                                    src={normalizedReview.avatar}
                                                    alt={normalizedReview.name}
                                                    className="w-8 h-8 rounded-full object-cover"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.nextElementSibling.style.display = 'flex';
                                                    }}
                                                />
                                                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold text-sm absolute top-0 left-0 hidden">
                                                    {generateInitials(normalizedReview.name)}
                                                </div>
                                            </>
                                        ) : (
                                            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                                                {generateInitials(normalizedReview.name)}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center space-x-2 mb-1">
                                            <div className="flex items-center space-x-1">
                                                <h4 className="font-semibold text-sm" style={{ color: reviewerNameColor }}>{normalizedReview.name}</h4>
                                                {normalizedReview.isVerified && (
                                                    <VerificationIcon
                                                        size="12"
                                                        accentColor={widgetConfig.style?.accentColor || '#4A90E2'}
                                                    />
                                                )}
                                            </div>
                                            <StarRating
                                                rating={normalizedReview.rating}
                                                size="w-3 h-3"
                                                color={widgetConfig.style?.rating?.starsColor || '#F1C40F'}
                                            />
                                            <PlatformIcon platform={normalizedReview.platform} className="w-3 h-3" />
                                        </div>
                                        <p style={{ color: textColor, fontSize: `${textSize}px` }}>{normalizedReview.comment}</p>
                                        <p className="text-xs mt-1" style={{ color: dateColor }}>{normalizedReview.date}</p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

// Sidebar Widget Preview
export const SidebarWidgetPreview = ({ reviews = [], previewMode = 'desktop', selectedPlatforms = [], widgetConfig = {} }) => {
    const displayReviews = (reviews.length > 0 && selectedPlatforms.length > 0)
        ? reviews.slice(0, 6) // Show more reviews for better preview
        : sampleReviews.slice(0, 3);

    // Generate container styles based on widgetConfig
    const getContainerStyles = () => {
        const backgroundConfig = widgetConfig.style?.background || {};
        const width = backgroundConfig.width || 800;
        const borderRadius = backgroundConfig.borderRadius ?? 16;
        const shadow = backgroundConfig.shadow ?? true;

        return {
            width: `${width}px`,
            maxWidth: '100%',
            borderRadius: `${borderRadius}px`,
            boxShadow: shadow ? '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' : 'none',
            fontFamily: getFontFamily(widgetConfig.style?.fontFamily || 'default')
        };
    };

    return (
        <div
            className={`mx-auto ${previewMode === 'mobile' ? 'flex flex-col' : 'flex'}`}
            style={getContainerStyles()}
        >
            <div className={`bg-gray-100 ${previewMode === 'mobile' ? 'p-4 rounded-t-lg min-h-[150px] flex-1' : 'flex-1 p-6 rounded-l-lg min-h-[300px]'}`}>
                <h3 className={`font-bold text-gray-900 mb-4 ${previewMode === 'mobile' ? 'text-base' : 'text-lg'}`}>Main Content</h3>
                <p className={`text-gray-600 ${previewMode === 'mobile' ? 'text-sm' : ''}`}>Your website content goes here...</p>
            </div>

            <div className={`bg-white shadow-sm ${previewMode === 'mobile' ? 'p-3 rounded-b-lg border-t w-full' : 'w-80 p-4 rounded-r-lg border-l'}`}>
                <div className={`space-y-4 overflow-y-auto ${previewMode === 'mobile' ? 'max-h-48' : 'max-h-64'}`}>
                    {displayReviews.map((review) => {
                        const normalizedReview = normalizeReviewData(review);
                        return (
                            <div key={normalizedReview.id} className="border-b border-gray-100 pb-3 last:border-b-0">
                                <div className="flex items-start space-x-2">
                                    <div className="w-6 h-6 rounded-full flex-shrink-0 relative">
                                        {isValidImageUrl(normalizedReview.avatar) ? (
                                            <>
                                                <img
                                                    src={normalizedReview.avatar}
                                                    alt={normalizedReview.name}
                                                    className="w-6 h-6 rounded-full object-cover"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.nextElementSibling.style.display = 'flex';
                                                    }}
                                                />
                                                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold text-xs absolute top-0 left-0 hidden">
                                                    {generateInitials(normalizedReview.name)}
                                                </div>
                                            </>
                                        ) : (
                                            <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold text-xs">
                                                {generateInitials(normalizedReview.name)}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center space-x-1 mb-1">
                                            <div className="flex items-center space-x-1">
                                                <h4 className="font-semibold text-gray-900 text-xs truncate">{normalizedReview.name}</h4>
                                                {normalizedReview.isVerified && (
                                                    <VerificationIcon
                                                        size="10"
                                                        accentColor={widgetConfig.style?.review?.verifiedBadgeColor || widgetConfig.style?.accentColor}
                                                    />
                                                )}
                                            </div>
                                            <StarRating
                                                rating={normalizedReview.rating}
                                                size="w-2 h-2"
                                                color={widgetConfig.style?.rating?.starsColor || '#F1C40F'}
                                            />
                                        </div>
                                        <p className="text-gray-700 text-xs line-clamp-2">{normalizedReview.comment}</p>
                                        <p className="text-xs text-gray-500 mt-1">{normalizedReview.date}</p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}; 